import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  isActiveColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { useGetGroupsAsOptionsQuery } from 'shared-pages/components/field-select/fs-cpgroup';
import {
  CompanyVatTitle,
  CpWasteCategoriesTitle,
  isAfisCyprus,
  isFotokiklosi,
  isMedicycle,
  isRebattery,
  regionTitle,
  isRebatteryLiionApp,
} from 'shared-pages/common/constants';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
    canEdit,
    injectFooterElement,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateContainer] = useMutation(gqlUpdate);
  const [addNewContainer] = useMutation(gqlAdd);
  const [deleteContainer] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Collectionpoint'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      ...(!isRebattery
        ? [
            {
              id: 'code',
              header: t('Κωδικός'),
              columnName: t('Κωδικός'),
              accessorKey: 'code',
              orderByField: 'code',
              cell: TableCellV8,
              size: 10,
            },
          ]
        : []),
      {
        id: 'name',
        header: t('Επωνυμία'),
        columnName: t('Επωνυμία'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 40,
        meta: { type: 'text' },
      },

      ...(isRebatteryLiionApp
        ? [
            {
              id: 'wasteCategories',
              header: t(CpWasteCategoriesTitle),
              columnName: t(CpWasteCategoriesTitle),
              accessorKey: 'wasteCategories',
              size: 40,
              cell: (props) => {
                const recs = props.row.original.collectionpointsWasteCategories ?? [];
                const names = recs
                  .map((v) => v.cpWasteCategory?.name)
                  .filter(Boolean)
                  .join(', ');
                return <div>{names}</div>;
              },
            },
          ]
        : []),

      /*       {
        id: 'cpGroup.name',
        header: t('Κατηγορία'),
        columnName: t('Κατηγορία'),
        accessorKey: 'cpGroup.name',
        orderByField: 'cpGroup.name',
        cell: TableCellV8,
        size: 40,
        meta: { type: 'text' },
      }, */

      ...(!isRebattery
        ? [
            {
              id: 'groupId',
              header: t('Κατηγορία'),
              columnName: t('Κατηγορία'),
              accessorKey: 'groupId',
              orderByField: 'cpGroup.name',
              cell: TableCellV8,
              size: 50,
              meta: {
                type: 'select',
                hook: useGetGroupsAsOptionsQuery,
                idKey: 'groupId',
                idName: 'cpGroup.name',
              },
            },
          ]
        : []),
      ...(isFotokiklosi
        ? [
            {
              id: 'addressDescription',
              header: t('Περιγραφή Διεύθυνσης'),
              columnName: t('Περιγραφή Διεύθυνσης'),
              accessorKey: 'addressDescription',
              orderByField: 'addressDescription',
              cell: TableCellV8,
              size: 20,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'addressLine1',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'addressLine1',
        orderByField: 'addressLine1',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'addressCity',
        header: t('Πόλη'),
        columnName: t('Πόλη'),
        accessorKey: 'addressCity',
        orderByField: 'addressCity',
        cell: TableCellV8,
        size: 30,
        meta: { type: 'text' },
      },
      ...(isAfisCyprus || isMedicycle
        ? [
            {
              id: 'region.name',
              header: t(regionTitle),
              columnName: t(regionTitle),
              accessorKey: 'region.name',
              orderByField: 'region.name',
              cell: TableCellV8,
              size: 30,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'phone',
        header: t('Τηλέφωνο'),
        columnName: t('Τηλέφωνο'),
        accessorKey: 'phone',
        orderByField: 'phone',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'companyVat',
        header: t(CompanyVatTitle),
        columnName: t(CompanyVatTitle),
        accessorKey: 'companyVat',
        orderByField: 'companyVat',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      ...(!isAfisCyprus && !isMedicycle
        ? [
            {
              id: 'companyTaxOffice',
              header: t('Δ.Ο.Υ.'),
              columnName: t('Δ.Ο.Υ.'),
              accessorKey: 'companyTaxOffice',
              orderByField: 'companyTaxOffice',
              cell: TableCellV8,
              size: 50,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'notes',
        header: t('Σχόλια'),
        columnName: t('Σχόλια'),
        accessorKey: 'notes',
        orderByField: 'notes',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      isActiveColumn(t, i18n),
      // firstDateUserColumn(t),
      // lastDateUserColumn(t),
      ...(!isGridInForm && canEdit
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                return true ? EditCellV8({ row, table }) : <>-</>;
              },
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Containers - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addContainertype.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Κάδο <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteContainer({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Κάδος');
            return true;
          }}
          injectFooterElement={injectFooterElement}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
